import { useCallback, useEffect, useRef, useState } from "react";
import { formatQuestion } from "../game/questions";
import { CORRECT_POINTS, ROUND_DURATION_MS, WRONG_POINTS, type Question, type RoundResult } from "../game/types";

interface DuelGameProps {
  player1Name: string;
  player2Name: string;
  questions: Question[];
  onFinish: (p1: RoundResult, p2: RoundResult) => void;
}

export function DuelGame({ player1Name, player2Name, questions, onFinish }: DuelGameProps) {
  const [indexes, setIndexes] = useState<[number, number]>([0, 0]);
  const [scores, setScores] = useState<[number, number]>([0, 0]);
  const [picked, setPicked] = useState<[number | null, number | null]>([null, null]);
  const [finished, setFinished] = useState<[boolean, boolean]>([false, false]);

  const scoresRef = useRef<[number, number]>([0, 0]);
  const correctCountsRef = useRef<[number, number]>([0, 0]);
  const wrongCountsRef = useRef<[number, number]>([0, 0]);
  const indexesRef = useRef<[number, number]>([0, 0]);
  const pickedRef = useRef<[number | null, number | null]>([null, null]);
  const doneRef = useRef(false);
  const startedAtRef = useRef(Date.now());

  const timerTextRef = useRef<HTMLSpanElement>(null);
  const timerBarRef = useRef<HTMLDivElement>(null);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    const [s1, s2] = scoresRef.current;
    const [c1, c2] = correctCountsRef.current;
    const [w1, w2] = wrongCountsRef.current;
    onFinish(
      { name: player1Name, score: s1, correctCount: c1, wrongCount: w1, timeUsedMs: ROUND_DURATION_MS },
      { name: player2Name, score: s2, correctCount: c2, wrongCount: w2, timeUsedMs: ROUND_DURATION_MS },
    );
  }, [player1Name, player2Name, onFinish]);

  // 60 FPS Direct-DOM Timer (0 React Re-renders while time ticks)
  useEffect(() => {
    startedAtRef.current = Date.now();
    let animId: number;

    const tick = () => {
      const elapsed = Date.now() - startedAtRef.current;
      const leftMs = Math.max(0, ROUND_DURATION_MS - elapsed);
      const secondsStr = (leftMs / 1000).toFixed(2);
      const ratio = leftMs / ROUND_DURATION_MS;

      if (timerTextRef.current) {
        timerTextRef.current.innerText = `${secondsStr}s`;
        if (leftMs < 10_000) {
          timerTextRef.current.classList.add("danger");
        } else {
          timerTextRef.current.classList.remove("danger");
        }
      }

      if (timerBarRef.current) {
        timerBarRef.current.style.width = `${ratio * 100}%`;
        if (leftMs < 10_000) {
          timerBarRef.current.classList.add("low");
        } else {
          timerBarRef.current.classList.remove("low");
        }
      }

      if (leftMs <= 0) {
        finish();
      } else {
        animId = requestAnimationFrame(tick);
      }
    };

    animId = requestAnimationFrame(tick);
    return () => {
      if (animId) cancelAnimationFrame(animId);
    };
  }, [finish]);

  const advance = (side: 0 | 1) => {
    pickedRef.current[side] = null;
    const next = indexesRef.current[side] + 1;
    if (next < questions.length) {
      indexesRef.current[side] = next;
      setIndexes(prev => {
        const copy: [number, number] = [...prev];
        copy[side] = next;
        return copy;
      });
    } else {
      setFinished(prev => {
        const copy: [boolean, boolean] = [...prev];
        copy[side] = true;
        return copy;
      });
    }
    setPicked(prev => {
      const copy: [number | null, number | null] = [...prev];
      copy[side] = null;
      return copy;
    });
  };

  const answer = (side: 0 | 1, value: number) => {
    if (doneRef.current || finished[side] || pickedRef.current[side] !== null) return;
    const currentQ = questions[indexesRef.current[side]];
    if (!currentQ) return;

    pickedRef.current[side] = value;
    setPicked(prev => {
      const copy: [number | null, number | null] = [...prev];
      copy[side] = value;
      return copy;
    });

    const ok = value === currentQ.answer;
    if (ok) {
      scoresRef.current[side] += CORRECT_POINTS;
      correctCountsRef.current[side] += 1;
    } else {
      scoresRef.current[side] += WRONG_POINTS;
      wrongCountsRef.current[side] += 1;
    }
    setScores(prev => {
      const copy: [number, number] = [...prev];
      copy[side] = scoresRef.current[side];
      return copy;
    });

    window.setTimeout(() => {
      if (doneRef.current) return;
      advance(side);
    }, 400);
  };

  const handleAnswerEvent = (e: React.SyntheticEvent, side: 0 | 1, value: number) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement)?.blur();
    (document.activeElement as HTMLElement)?.blur();
    answer(side, value);
  };

  const renderPanel = (side: 0 | 1) => {
    const name = side === 0 ? player1Name : player2Name;
    const question = questions[indexes[side]];
    const sidePicked = picked[side];
    const answered = sidePicked !== null;
    const ok = answered && question && sidePicked === question.answer;
    const opClass = question ? `op-${question.op}` : "";
    const opSymbol = question ? (question.op === "add" ? "+" : question.op === "sub" ? "−" : question.op === "mul" ? "×" : "÷") : "";

    return (
      <section className={`duel-panel ${opClass} ${side === 1 ? "right" : ""}`}>
        <div className="duel-head">
          <span className="badge">P{side + 1}</span>
          <span className="player-name">{name}</span>
          {question && <span className={`op-tag op-tag-${question.op}`}>{opSymbol}</span>}
          <div className="stat-box">
            <span className="stat-label">Score</span>
            <span className="stat-value">{scores[side]}</span>
          </div>
        </div>

        {finished[side] || !question ? (
          <div className="set-done">
            <div className="set-done-score">{scores[side]}</div>
            <div className="set-done-label">Set complete!</div>
          </div>
        ) : (
          <>
            <div className="question-display">
              <span className="question">{formatQuestion(question)}</span>
              <span className="equals">=</span>
              <span className="question-mark">?</span>
            </div>

            <div className="options-grid">
              {question.options.map((opt, i) => {
                let cls = "option-btn";
                if (answered) {
                  if (opt === question.answer) cls += " correct";
                  else if (opt === sidePicked) cls += " wrong";
                  else cls += " dim";
                }
                return (
                  <button
                    key={i}
                    type="button"
                    className={cls}
                    disabled={answered}
                    onPointerDown={e => handleAnswerEvent(e, side, opt)}
                    onClick={e => handleAnswerEvent(e, side, opt)}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>

            <div className={`feedback ${answered ? (ok ? "correct" : "wrong") : ""}`}>
              {answered &&
                (ok ? (
                  <span className="feedback-text">Correct!</span>
                ) : (
                  <span className="feedback-text">
                    Oops! = {question.answer}
                  </span>
                ))}
            </div>
          </>
        )}
      </section>
    );
  };

  return (
    <div className="duel-game">
      <header className="game-header">
        <span className="duel-timer">
          <span ref={timerTextRef} className="stat-value">60.00s</span>
        </span>
        <div className="timer-bar">
          <div ref={timerBarRef} className="timer-bar-fill" style={{ width: "100%" }} />
        </div>
      </header>

      <div className="duel-layout">
        {renderPanel(0)}
        {renderPanel(1)}
      </div>

      <p className="game-hint">Multitouch enabled — answer simultaneously on your side</p>
    </div>
  );
}
